/**
 * Asse dei giorni di un itinerario.
 *
 * Un itinerario collegato a una travel request vive su date reali; un template
 * (itinerario orfano del Playground) vive su giorni relativi 1-based e non ha date:
 * le acquisisce solo quando viene collegato a una richiesta.
 *
 * Il builder indicizza i giorni per **chiave**, non per data: in modalità `dates` la
 * chiave è la data ISO (comportamento storico), in modalità `index` è `D<n>`.
 * Il formato della chiave non esce da questo modulo: chi ha un indice usa
 * `templateDayKey`, chi ha una chiave usa `templateDayIndex`.
 */

import { addDays, diffDays, inclusiveDayCount } from '@/components/utils/dateUtils';

/** Itinerario collegato a una travel request: i giorni sono date reali. */
export type DatesAxis = { mode: 'dates'; startDate: string; endDate: string };
/** Template: i giorni sono posizioni 1-based e le date non esistono ancora. */
export type TemplateAxis = { mode: 'index'; durationDays: number };

export type DayAxis = DatesAxis | TemplateAxis;

/** Prefisso delle chiavi giorno in modalità template: non è mai una data valida. */
const TEMPLATE_KEY_PREFIX = 'D';

export const datesAxis = (startDate: string, endDate: string): DatesAxis => ({
  mode: 'dates',
  startDate,
  endDate,
});

export const indexAxis = (durationDays: number): TemplateAxis => ({
  mode: 'index',
  durationDays: Math.max(1, Math.trunc(durationDays || 1)),
});

/**
 * Costruisce l'asse a partire dai campi dell'itinerario.
 * `duration_days` valorizzato = template; altrimenti date reali.
 */
export const axisFromItinerary = (itinerary: {
  start_date?: string | null;
  end_date?: string | null;
  duration_days?: number | null;
}): DayAxis | null => {
  if (itinerary.duration_days) return indexAxis(itinerary.duration_days);
  if (itinerary.start_date && itinerary.end_date) {
    return datesAxis(itinerary.start_date, itinerary.end_date);
  }
  return null;
};

/** Type guard: restringe l'asse a quello dei template, così i chiamanti non devono castare. */
export const isTemplateAxis = (axis: DayAxis | null | undefined): axis is TemplateAxis =>
  axis?.mode === 'index';

// ---------------------------------------------------------------------------
// Chiavi giorno dei template
//
// Le due funzioni qui sotto sono l'unico posto che conosce il formato della
// chiave: servono a chi non ha un asse sottomano (DTO, diff di salvataggio).
// ---------------------------------------------------------------------------

/** Chiave del giorno relativo `index` (1-based). */
export const templateDayKey = (index: number): string => `${TEMPLATE_KEY_PREFIX}${index}`;

/** Indice 1-based codificato in una chiave di template. */
export const templateDayIndex = (key: string): number =>
  Number(key.slice(TEMPLATE_KEY_PREFIX.length)) || 1;

/** Una chiave giorno di un template (`D3`), da distinguere da una data ISO. */
export const isTemplateDayKey = (key: string | null | undefined): boolean =>
  !!key && key.startsWith(TEMPLATE_KEY_PREFIX);

// ---------------------------------------------------------------------------
// Conversioni chiave <-> indice <-> data
// ---------------------------------------------------------------------------

/** Chiave del giorno `index` (1-based) sull'asse. */
export const dayKeyFromIndex = (axis: DayAxis, index: number): string =>
  axis.mode === 'index' ? templateDayKey(index) : addDays(axis.startDate, index - 1);

/** Indice 1-based del giorno identificato dalla chiave. */
export const dayIndexFromKey = (axis: DayAxis, key: string): number =>
  axis.mode === 'index' ? templateDayIndex(key) : diffDays(axis.startDate, key) + 1;

/** Data reale del giorno, oppure null su un template. */
export const dayDateFromKey = (axis: DayAxis, key: string): string | null =>
  axis.mode === 'index' ? null : key;

/**
 * Chiave del giorno in cui cade un'attività, qualunque sia il suo ancoraggio.
 *
 * Per le attività multi-giorno conta il giorno d'inizio: è quello su cui il builder
 * le colloca (il check-out ha una voce propria, con il suo ancoraggio).
 */
export const dayKeyFromActivity = (
  axis: DayAxis,
  activity: {
    date?: string | null;
    start_date?: string | null;
    day_index?: number | null;
    start_day_index?: number | null;
  },
): string | null => {
  if (axis.mode === 'index') {
    const index = activity.start_day_index ?? activity.day_index;
    return index ? templateDayKey(index) : null;
  }
  const date = activity.start_date || activity.date;
  return date ? date.split('T')[0] : null;
};

/** Numero di giorni previsti dall'asse. */
export const axisDayCount = (axis: DayAxis): number =>
  axis.mode === 'index' ? axis.durationDays : inclusiveDayCount(axis.startDate, axis.endDate);

/** Tutte le chiavi giorno dell'asse, in ordine. */
export const axisDayKeys = (axis: DayAxis): string[] =>
  Array.from({ length: axisDayCount(axis) }, (_, i) => dayKeyFromIndex(axis, i + 1));

/**
 * Ordinamento fra chiavi giorno: le date ISO si confrontano lessicalmente, gli
 * indici vanno confrontati come numeri (`D10` viene dopo `D9`, non prima).
 */
export const compareDayKeys = (a: string, b: string): number => {
  if (isTemplateDayKey(a) && isTemplateDayKey(b)) {
    return templateDayIndex(a) - templateDayIndex(b);
  }
  return a.localeCompare(b);
};

// ---------------------------------------------------------------------------
// Ancoraggio delle attività
// ---------------------------------------------------------------------------

/**
 * I campi con cui un'attività dichiara a quale giorno appartiene: una data reale
 * oppure un intervallo di giorni, mai entrambi. Costruirli solo con le funzioni qui
 * sotto è ciò che tiene l'invariante in un posto solo (e coerente col CHECK a database).
 *
 * Su un template l'ancoraggio è **sempre** un intervallo: un'attività che dura un
 * giorno solo ha `start_day_index === end_day_index`. `day_index` non compare qui
 * perché a database è una colonna generata da `start_day_index`: si legge, non si scrive.
 */
export interface DayAnchor {
  date: string;
  start_day_index: number | null;
  end_day_index: number | null;
}

/** Estensione dell'ancoraggio alle attività che coprono più giorni con date reali (alloggi). */
export interface DayRangeAnchor extends DayAnchor {
  start_date?: string;
  end_date?: string;
}

/**
 * Durata di un'attività in giorni, estremi inclusi: 1 quando dura un giorno solo.
 * Legge l'intervallo dove c'è, altrimenti lo deduce dalle date reali.
 */
export const activityDaySpan = (activity: {
  start_date?: string | null;
  end_date?: string | null;
  start_day_index?: number | null;
  end_day_index?: number | null;
}): number => {
  const { start_day_index: start, end_day_index: end } = activity;
  if (start && end) return Math.max(1, end - start + 1);
  if (activity.start_date && activity.end_date) {
    return Math.max(1, inclusiveDayCount(activity.start_date, activity.end_date));
  }
  return 1;
};

/**
 * Ancoraggio di un'attività al giorno indicato, lunga `days` giorni (default: 1).
 *
 * Su un template scrive sempre l'intervallo completo — start = end per chi dura un
 * giorno solo. Su un itinerario a date reali scrive la sola `date`: `start_date`/`end_date`
 * restano quelle che l'attività si porta dietro dalla modale.
 */
export const dayAnchorForActivity = (axis: DayAxis, dayKey: string, days = 1): DayAnchor => {
  if (axis.mode !== 'index') return { date: dayKey, start_day_index: null, end_day_index: null };
  const startIndex = dayIndexFromKey(axis, dayKey);
  return {
    date: '',
    start_day_index: startIndex,
    end_day_index: startIndex + (Math.max(1, Math.trunc(days || 1)) - 1),
  };
};

/**
 * Ancoraggio di un soggiorno: dal giorno indicato per `nights` notti.
 * Il check-out cade il giorno dopo l'ultima notte, sia in date sia in indici.
 */
export const stayAnchor = (axis: DayAxis, dayKey: string, nights: number): DayRangeAnchor => {
  const stayNights = Math.max(1, nights);

  if (axis.mode === 'index') {
    const startIndex = dayIndexFromKey(axis, dayKey);
    return {
      date: '',
      start_day_index: startIndex,
      end_day_index: startIndex + stayNights,
    };
  }

  return {
    date: dayKey,
    start_date: dayKey,
    end_date: addDays(dayKey, stayNights),
    start_day_index: null,
    end_day_index: null,
  };
};

/** Un'attività è collocabile solo se ha un ancoraggio: una data o un giorno d'inizio. */
export const hasDayAnchor = (activity: {
  date?: string | null;
  start_day_index?: number | null;
  day_index?: number | null;
}): boolean => !!(activity.date || activity.start_day_index || activity.day_index);

// Riesportate perché i consumatori dell'asse ragionano quasi sempre in giorni.
export { addDays, diffDays, inclusiveDayCount };
